import { JobStatusCellRendererProps } from '../../../../types/frontend/cell-renderers/JobStatusCellRenderer.types';
import { JobStatus } from '../../../../types/backend/jobStatus';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../../../core/store/root.store';
import { HostRunFailureReason } from '@goto/remote-execution';
import en from '../../../../../i18n/locales/en-US.json';

interface UseJobStatusCellRendererReturnType {
  statusText: string;
  statisticsText: string;
}

const jobStatusToStatisticsKeyMap: Record<JobStatus, string> = {
  [JobStatus.None]: 'jobHistoryGrid.jobRunStatistics.unknown',
  [JobStatus.Scheduled]: 'jobHistoryGrid.jobRunStatistics.scheduled',
  [JobStatus.Running]: 'jobHistoryGrid.jobRunStatistics.running',
  [JobStatus.Successful]: 'jobHistoryGrid.jobRunStatistics.successful',
  [JobStatus.PartiallySuccessful]: 'jobHistoryGrid.jobRunStatistics.partiallySuccessful',
  [JobStatus.Failed]: 'jobHistoryGrid.jobRunStatistics.failed',
  [JobStatus.Cancelled]: 'jobHistoryGrid.jobRunStatistics.cancelled'
};

type Translations = typeof en;
const jobStatusTextMap: Record<JobStatus, keyof Translations['jobHistoryGrid']['jobStatus']> = {
  [JobStatus.None]: 'unknown',
  [JobStatus.Scheduled]: 'scheduled',
  [JobStatus.Running]: 'running',
  [JobStatus.Successful]: 'successful',
  [JobStatus.PartiallySuccessful]: 'partiallySuccessful',
  [JobStatus.Failed]: 'failed',
  [JobStatus.Cancelled]: 'cancelled'
};

export const useJobStatusCellRenderer = ({
  isOsUpdate,
  jobRunStatistics,
  jobStatus,
  status
}: JobStatusCellRendererProps): UseJobStatusCellRendererReturnType => {
  const { t } = useTranslation();
  const { partiallySuccessfulHistoryEnabled } = useAppSelector(state => state.uiFrame.features);

  const statusText = useMemo((): string => {
    if (partiallySuccessfulHistoryEnabled && status?.failureReason === HostRunFailureReason.HostOffline) {
      return t('updateDetails.grid.deviceJobFailureReason.hostOffline.title');
    }

    return t(`jobHistoryGrid.jobStatus.${jobStatus ? jobStatusTextMap[jobStatus] : jobStatusTextMap[JobStatus.None]}`);
  }, [status, jobStatus, t, partiallySuccessfulHistoryEnabled]);

  const statisticsText = useMemo((): string => {
    const key = jobStatus ? jobStatusToStatisticsKeyMap[jobStatus] : 'jobHistoryGrid.jobRunStatistics.unknown';
    const singularOrPlural =
      jobRunStatistics?.successfulUpdateCount && jobRunStatistics?.successfulUpdateCount > 1 ? 'plural' : 'singular';
    if (
      jobStatus === JobStatus.Successful ||
      jobStatus === JobStatus.PartiallySuccessful ||
      jobStatus === JobStatus.Failed
    ) {
      if (isOsUpdate) {
        return t(`${key}.${singularOrPlural}.osUpdate` as never, {
          successfulUpdateCount: jobRunStatistics?.successfulUpdateCount,
          totalUpdateCount: jobRunStatistics?.totalUpdateCount
        }) as string;
      } else {
        return t(`${key}.${singularOrPlural}.appUpdate` as never, {
          successfulUpdateCount: jobRunStatistics?.successfulUpdateCount,
          totalUpdateCount: jobRunStatistics?.totalUpdateCount
        }) as string;
      }
    }
    return t(key as never) as string;
  }, [jobStatus, t, isOsUpdate, jobRunStatistics]);

  return {
    statusText,
    statisticsText
  };
};
